import { useState, useEffect } from "react";
import PageSeo from "@/components/seo/PageSeo";
import { Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Award, 
  BookOpen, 
  Users, 
  CheckCircle, 
  Globe, 
  Sparkles,
  Menu,
  X
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import Footer from "@/components/layout/Footer";
import axios from "axios";
import { API } from "@/App";
import TradingHorizonHeroBg from "@/components/hero/TradingHorizonHeroBg";

const AboutPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [SC, setSC] = useState({});

  const navLinks = [
    { label: "Home", href: "/" },
    { label: "About", href: "/about" },
    { label: "Courses", href: "/#courses" },
    { label: "Blogs", href: "/blog" },
    { label: "Gallery", href: "/gallery" },
    { label: "Contact", href: "/contact" }
  ];

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/site-config`);
        setSC(res.data || {});
      } catch (err) {
        console.error("Failed to fetch site config", err);
      }
    };
    fetchConfig();

    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#CBD5E1] font-plex overflow-x-hidden selection:bg-[#00FF88]/20 selection:text-white">
      <PageSeo path="/about" fallbackTitle="About Jamadhan" fallbackDescription="Learn about Jamadhan's mission to make trading education accessible." />
      
      {/* GLOWS */}
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-[#00FF88]/5 rounded-full blur-[120px] pointer-events-none z-0" />
      <div className="absolute top-[30%] right-1/4 w-[500px] h-[500px] bg-[#00F0FF]/5 rounded-full blur-[150px] pointer-events-none z-0" />
      <div className="absolute bottom-[20%] left-1/3 w-[600px] h-[600px] bg-[#00FF88]/3 rounded-full blur-[180px] pointer-events-none z-0" />

      {/* ═══ FULL SITE HEADER / NAVBAR ═══ */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#050505]/90 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.2)] border border-[#00FF88]/20 group-hover:border-[#00FF88]/60 transition-all duration-300">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-lg font-bold text-white font-outfit tracking-tight group-hover:text-[#00FF88] transition-colors">JAMADHAN</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((l) => (
              <Link 
                key={l.label} 
                to={l.href} 
                className={`text-sm font-medium tracking-wide transition-colors ${
                  l.href === '/about' ? 'text-[#00FF88] font-bold' : 'text-[#CBD5E1] hover:text-[#00FF88]'
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/drishti-ai" className="text-sm text-[#00F0FF] hover:text-[#00FF88] transition-colors font-semibold flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Drishti AI
            </Link>
            <Button 
              onClick={() => navigate('/login')} 
              size="sm" 
              className="bg-gradient-to-r from-[#00FF88] to-[#00C853] text-black font-bold hover:shadow-[0_0_20px_rgba(0,255,136,0.4)] border border-transparent rounded-sm px-5 transition-all duration-200"
            >
              Login
            </Button>
          </div>

          <button 
            onClick={() => setMobileMenu(!mobileMenu)} 
            className="md:hidden text-white p-1 hover:text-[#00FF88] transition-colors" 
            aria-label="Toggle Menu"
          >
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenu && (
          <div className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10 px-6 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <Link 
                key={l.label} 
                to={l.href} 
                onClick={() => setMobileMenu(false)}
                className={`text-sm font-medium py-1.5 transition-colors ${
                  l.href === '/about' ? 'text-[#00FF88] font-bold' : 'text-[#CBD5E1] hover:text-[#00FF88]'
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link 
              to="/drishti-ai" 
              onClick={() => setMobileMenu(false)}
              className="text-sm text-[#00F0FF] hover:text-[#00FF88] transition-colors font-semibold flex items-center gap-1 py-1.5"
            >
              <Sparkles className="w-3.5 h-3.5" /> Drishti AI
            </Link>
            <Button 
              onClick={() => { setMobileMenu(false); navigate('/login'); }} 
              size="sm" 
              className="mt-2 w-full bg-[#00FF88] text-black font-bold"
            >
              Login
            </Button>
          </div>
        )}
      </nav>

      {/* ═══ HERO WITH TRADINGVIEW-GRADE EARTH HORIZON & CANDLESTICKS ═══ */}
      <TradingHorizonHeroBg className="pt-36 pb-28 px-6 min-h-[90vh] flex flex-col items-center justify-center border-b border-white/5">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: 15 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/35 text-[#00FF88] text-xs font-semibold tracking-wider uppercase mb-8 font-jetbrains backdrop-blur-md shadow-[0_0_20px_rgba(0,255,136,0.2)]"
          >
            <Sparkles className="w-3.5 h-3.5" /> India's Premier Institutional Trading Institute
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 25 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-black text-white font-outfit leading-[1.05] tracking-tight mb-8 drop-shadow-2xl"
            dangerouslySetInnerHTML={{ __html: SC.about_hero_heading || "We Don't Predict Markets.<br /><span class=\"bg-gradient-to-r from-[#00FF88] via-[#00F0FF] to-[#00FF88] bg-clip-text text-transparent drop-shadow-[0_2px_25px_rgba(0,255,136,0.4)]\">We Master Them.</span>" }}
          />

          <motion.p 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-[#CBD5E1] max-w-3xl mx-auto mb-10 leading-relaxed font-outfit font-light drop-shadow-md"
          >
            {SC.about_hero_description || "India's leading stock market institute empowering traders with institutional demand & supply zone analytics, disciplined execution, and the confidence to master the markets."}
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button 
              onClick={() => scrollToId("our-story-section")}
              size="lg" 
              className="w-full sm:w-auto bg-[#00FF88] text-black font-extrabold hover:bg-white hover:shadow-[0_0_30px_rgba(0,255,136,0.6)] border border-transparent rounded-sm px-8 text-base transition-all duration-200 shadow-xl"
            >
              Our Story
            </Button>
            <Button 
              variant="outline" 
              onClick={() => scrollToId("mentor-section")}
              size="lg" 
              className="w-full sm:w-auto border-[#00FF88]/50 text-[#00FF88] hover:bg-[#00FF88]/15 backdrop-blur-md rounded-sm px-8 text-base transition-all font-bold"
            >
              Meet Your Mentor &rarr;
            </Button>
          </motion.div>
        </div>
      </TradingHorizonHeroBg>

      <section className="relative z-10 -mt-10 max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-8 bg-[#0D121F]/80 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl">
          {[
            { value: SC.about_stat_1_value || "12+", label: SC.about_stat_1_label || "Years of Expertise" },
            { value: SC.about_stat_2_value || "10,000+", label: SC.about_stat_2_label || "Traders Transformed" },
            { value: SC.about_stat_3_value || "6", label: SC.about_stat_3_label || "Markets Covered" },
            { value: SC.about_stat_4_value || "ISO", label: SC.about_stat_4_label || "Certified 21001:2018" }
          ].map((stat, idx) => (
            <div key={idx} className="text-center p-4 border-r border-white/5 last:border-0 md:even:border-r-0 lg:even:border-r">
              <div className="text-3xl sm:text-4xl font-extrabold font-outfit bg-gradient-to-r from-[#00FF88] to-[#00F0FF] bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-[#A1A1AA] uppercase tracking-wider mt-2 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OUR STORY */}
      <section id="our-story-section" className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/25 text-xs font-semibold uppercase tracking-widest font-jetbrains">
              <span className="w-1.5 h-1.5 bg-[#00FF88] rounded-full animate-ping" />
              Our Story
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-outfit text-white leading-tight">
              {SC.about_story_heading || "Born to Break the Myth"}
            </h2>

            <div className="space-y-4 text-base sm:text-lg text-[#A1A1AA] leading-relaxed whitespace-pre-wrap">
              {SC.about_story_description || "JamaDhan was created with a single, powerful purpose — to demolish the dangerous myth that the stock market is a gamble reserved for the lucky and the rich.\n\nWe've seen too many hardworking Indians lose their savings chasing tips, following news channels, and acting on rumors. That ends here. The stock market is a skill — learnable, repeatable, and profitable when approached with the right foundation.\n\nSince 2021, we have transformed complete beginners into confident, independent traders using a curriculum built on mathematics, discipline, and real market experience — not luck, not speculation."}
            </div>

            <div className="flex flex-wrap gap-2.5 pt-2">
              {["Indian Equity", "Options Trading", "Cryptocurrency", "Commodities", "Crude & Metals", "Forex"].map((tag) => (
                <span 
                  key={tag} 
                  className="px-3.5 py-1.5 text-xs font-medium text-white/90 bg-[#131B2C]/90 hover:bg-[#00FF88]/10 hover:text-[#00FF88] border border-white/5 hover:border-[#00FF88]/30 rounded-full transition-all duration-300 cursor-default"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              <div className="p-4 rounded bg-[#0D121F] border border-white/5 hover:border-[#00FF88]/30 transition-all duration-300">
                <div className="text-white font-semibold font-outfit flex items-center gap-2 mb-1.5">
                  <span className="text-[#00FF88]">📍</span> Offline Classes
                </div>
                <p className="text-xs text-[#8E9CAE]">In-person training in Jaipur</p>
              </div>
              <div className="p-4 rounded bg-[#0D121F] border border-white/5 hover:border-[#00FF88]/30 transition-all duration-300">
                <div className="text-white font-semibold font-outfit flex items-center gap-2 mb-1.5">
                  <span className="text-[#00FF88]">💻</span> Online Classes
                </div>
                <p className="text-xs text-[#8E9CAE]">Learn from anywhere in India</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="relative group overflow-hidden rounded-lg border border-white/10 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10 opacity-70" />
              <img 
                src="/about_deepak.webp" 
                alt="Deepak Sethia" 
                className="w-full h-[280px] object-cover object-top group-hover:scale-105 transition-transform duration-700" 
              />
              <div className="absolute bottom-4 left-4 z-20">
                <span className="text-xs font-bold uppercase tracking-widest text-[#00FF88]">Deepak Sethia</span>
                <p className="text-xs text-white/70">Founder &amp; Head Mentor</p>
              </div>
            </div>

            <div className="p-6 rounded-lg bg-gradient-to-br from-[#131B2C] to-[#0D121F] border-l-4 border-[#00FF88] border-y border-r border-white/5 shadow-xl relative overflow-hidden">
              <div className="absolute -right-6 -bottom-6 text-white/5 font-serif text-8xl pointer-events-none">"</div>
              <p className="text-white font-outfit text-base italic leading-relaxed mb-4 relative z-10">
                "{SC.about_founder_quote || "The stock market is not a casino. It's a science — and science can be learned."}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#00FF88] flex items-center justify-center font-bold text-black text-xs">
                  DS
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white font-outfit">Deepak Sethia</h4>
                  <p className="text-[11px] text-[#00FF88] uppercase tracking-wider">Founder, JamaDhan</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="py-20 px-6 bg-[#090D16] border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#00FF88] text-xs font-bold uppercase tracking-widest font-jetbrains">
              What Drives Us
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-outfit text-white mt-3">
              Vision &amp; Mission
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="p-8 rounded-lg bg-[#05080E]/70 border border-white/10 hover:border-[#00FF88]/50 shadow-xl transition-all duration-300 relative group flex flex-col justify-between">
              <div>
                <span className="text-[11px] font-bold text-[#00FF88] uppercase tracking-widest">Our Vision</span>
                <h3 className="text-2xl font-bold font-outfit text-white mt-2 mb-4 group-hover:text-[#00FF88] transition-colors">
                  {SC.about_vision_heading || "Trading Is Not Gambling"}
                </h3>
                <p className="text-[#A1A1AA] text-sm sm:text-base leading-relaxed">
                  {SC.about_vision_desc || "We envision a world where stock trading is recognised as a legitimate, knowledge-based profession — not viewed as gambling or speculation. Our goal is to spread financial literacy so broadly that every Indian trader enters the market with confidence, strategy, and clarity."}
                </p>
              </div>
              <div className="w-12 h-1 bg-[#00FF88] mt-6 group-hover:w-full transition-all duration-300" />
            </div>

            <div className="p-8 rounded-lg bg-[#05080E]/70 border border-white/10 hover:border-[#00F0FF]/50 shadow-xl transition-all duration-300 relative group flex flex-col justify-between">
              <div>
                <span className="text-[11px] font-bold text-[#00F0FF] uppercase tracking-widest">Our Mission</span>
                <h3 className="text-2xl font-bold font-outfit text-white mt-2 mb-4 group-hover:text-[#00F0FF] transition-colors">
                  {SC.about_mission_heading || "Build a Strong Foundation First"}
                </h3>
                <p className="text-[#A1A1AA] text-sm sm:text-base leading-relaxed">
                  {SC.about_mission_desc || "Just as a building's strength starts with its foundation, every trader's success begins with deep knowledge and proper education. Our mission is simple: no student ever enters the market underprepared."}
                </p>
              </div>
              <div className="mt-6 p-4 rounded bg-[#0D121F] border border-white/5">
                <div className="text-[11px] text-[#8E9CAE] uppercase tracking-widest mb-1">Hindi Proverb</div>
                <div className="text-[#00F0FF] font-outfit font-bold text-base">
                  मकान की नींव मजबूत होनी चाहिए
                </div>
                <div className="text-xs text-[#8E9CAE] italic mt-1">
                  Translation: The foundation of a house must be strong
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* INSTRUCTOR */}
      <section id="mentor-section" className="py-24 px-6 max-w-7xl mx-auto">
        <div className="mb-12">
          <span className="text-[#00FF88] text-xs font-bold uppercase tracking-widest font-jetbrains">
            Know Your Instructor
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-outfit text-white mt-3">
            {SC.about_mentor_heading || "Deepak Doesn't Teach Tips. He Teaches Truth."}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <div className="bg-[#0D121F] border border-white/10 rounded-lg p-6 overflow-hidden shadow-2xl relative">
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#00FF88]/5 rounded-bl-full pointer-events-none" />
              
              <div className="w-full h-[320px] rounded overflow-hidden mb-6 border border-white/5 relative">
                <img 
                  src="/about_concept.jpg" 
                  alt="Trading Concept" 
                  className="w-full h-full object-cover object-center" 
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded bg-[#00FF88] text-black text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-md">
                  NISM Certified ✓
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold font-outfit text-white">Deepak Sethia</h3>
                <p className="text-sm text-[#00FF88] font-semibold mt-1">Founder &amp; Head Mentor</p>
                
                <div className="mt-6 space-y-4 border-t border-white/5 pt-4">
                  {[
                    { val: "12+", label: "Years of active trading experience" },
                    { val: "10K+", label: "Professional traders trained" },
                    { val: "2021", label: "Founded JamaDhan Institute" }
                  ].map((instStat, idx) => (
                    <div key={idx} className="flex gap-4 items-center">
                      <div className="text-xl font-bold text-[#00FF88] font-outfit w-16 text-right shrink-0">
                        {instStat.val}
                      </div>
                      <div className="text-xs text-[#8E9CAE]">
                        {instStat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 space-y-12">
            <div className="space-y-6 text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              <p>
                With over 12 years of live trading experience across Indian equity, options, commodities, and international markets, Deepak Sethia has built something rare — a track record that speaks in numbers, not promises.
              </p>
              <p>
                Having personally trained more than 10,000 traders, Deepak understands the exact point where most people fail: they enter markets looking for shortcuts. His teaching philosophy dismantles that approach completely.
              </p>
              <p>
                Every class with Deepak is grounded in calculated math, risk management, and market reality. No tips. No noise. Just the systems that actually work — consistently, across any market condition.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold font-outfit text-white mb-6 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#00FF88] rounded-full" />
                Teaching &amp; Trading Philosophy
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  {
                    title: "Trading Philosophy",
                    desc: "Calculated math over gut feeling. Every trade is a decision backed by data, not emotion or rumour."
                  },
                  {
                    title: "Teaching Approach",
                    desc: "From basic concepts to advanced strategies — built to ensure you never trade without complete clarity."
                  },
                  {
                    title: "Student Promise",
                    desc: "Every student leaves with a system they can execute independently, for life."
                  },
                  {
                    title: "Market Coverage",
                    desc: "Equity, options, crypto, commodities, bullion, forex — one framework that works across all markets."
                  }
                ].map((philo, idx) => (
                  <div key={idx} className="p-6 rounded-lg bg-[#090D16] border border-white/5 hover:border-[#00FF88]/35 transition-all duration-300">
                    <h4 className="text-lg font-bold font-outfit text-white mb-2">{philo.title}</h4>
                    <p className="text-sm text-[#8E9CAE] leading-relaxed">{philo.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* WHY JAMADHAN */}
      <section className="py-24 px-6 bg-[#090D16] border-y border-white/5 relative">
        <div className="max-w-7xl mx-auto">
          
          <div className="max-w-3xl mb-16">
            <span className="text-[#00FF88] text-xs font-bold uppercase tracking-widest font-jetbrains">
              Why JamaDhan
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-outfit text-white mt-3 mb-6">
              The Difference Is in the Detail
            </h2>
            <p className="text-[#A1A1AA] text-base sm:text-lg leading-relaxed">
              We don't just hand you a curriculum. We immerse you in a complete trading ecosystem — from your very first concept to your first confident trade in live markets.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Award className="w-8 h-8 text-[#00FF88]" />,
                title: "Expert Instructors",
                desc: "Learn directly from active market professionals with real skin in the game — not textbook teachers. Every lesson comes from lived experience in real markets."
              },
              {
                icon: <BookOpen className="w-8 h-8 text-[#00FF88]" />,
                title: "Comprehensive Curriculum",
                desc: "From understanding your first candlestick to executing advanced options strategies — our curriculum is designed for complete mastery, not surface-level awareness."
              },
              {
                icon: <Users className="w-8 h-8 text-[#00FF88]" />,
                title: "Personalised Mentorship",
                desc: "One-on-one guidance tailored to your pace, your capital, and your goals. Your learning journey is not the same as everyone else's — and neither is our support."
              },
              {
                icon: <Globe className="w-8 h-8 text-[#00FF88]" />,
                title: "Community of Traders",
                desc: "Join thousands of like-minded traders sharing strategies, insights, and growth. The market is complex — having the right community makes all the difference."
              }
            ].map((feat, idx) => (
              <div 
                key={idx} 
                className="p-6 rounded-lg bg-[#05080E]/90 border border-white/5 hover:border-[#00FF88]/35 shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  <div className="mb-4 bg-[#00FF88]/10 w-14 h-14 rounded-sm flex items-center justify-center border border-[#00FF88]/20">
                    {feat.icon}
                  </div>
                  <h3 className="text-lg font-bold font-outfit text-white mb-3">{feat.title}</h3>
                  <p className="text-xs sm:text-sm text-[#8E9CAE] leading-relaxed">{feat.desc}</p>
                </div>
                <div className="h-0.5 w-8 bg-[#00FF88] mt-6" />
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* UNLEASH THE BULL */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <div className="lg:col-span-7 space-y-6">
            <span className="text-[#00FF88] text-xs font-bold uppercase tracking-widest font-jetbrains">
              Your Potential
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-outfit text-white">
              Don't Keep Your Potential Caged
            </h2>
            <p className="text-lg text-[#00F0FF] italic font-outfit font-light">
              There's a Bull Inside Everyone — Unleash it with Proper Training
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6">
              {[
                { title: "Expert-Led Training", desc: "Seasoned professionals sharing real insights and battle-tested strategies" },
                { title: "In-Depth Modules", desc: "From trading basics to advanced strategies — nothing is left uncovered" },
                { title: "Practical Application", desc: "Hands-on exercises and simulated trading that bridges theory and live markets" },
                { title: "Community Support", desc: "A network that grows with you — share, learn, and succeed together" }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00FF88] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-white font-outfit">{item.title}</h4>
                    <p className="text-xs text-[#8E9CAE] mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-6">
              <Button 
                onClick={() => navigate('/login')} 
                size="lg" 
                className="bg-gradient-to-r from-[#00FF88] to-[#00C853] text-black font-bold hover:shadow-[0_0_25px_rgba(0,255,136,0.5)] border border-transparent rounded-sm px-8"
              >
                Begin Your Trading Journey
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-1.5 bg-gradient-to-r from-[#00FF88] to-[#00F0FF] rounded-lg blur opacity-20 transition duration-1000" />
            <div className="relative bg-[#05080E] border border-white/10 rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="/about_bull.jpg" 
                alt="JamaDhan Bull - Unleash Your Potential" 
                className="w-full h-[400px] object-cover object-center" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#00FF88] bg-[#00FF88]/10 border border-[#00FF88]/25 px-2.5 py-1 rounded">Unleash The Bull</span>
                <h4 className="text-xl font-bold font-outfit text-white mt-3">Break Free. Trade Smart.</h4>
                <p className="text-xs text-white/70 mt-1">Master the markets with the right knowledge, strategy and discipline.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#090D16] to-[#050505] text-center border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,136,0.015)_1px,transparent_1px)] bg-[size:100px_100px] pointer-events-none" />
        
        <div className="relative z-10 max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-bold font-outfit text-white mb-6">
            Ready to Make the Market Work for You?
          </h2>
          <p className="text-base sm:text-lg text-[#A1A1AA] max-w-2xl mx-auto mb-10 leading-relaxed font-outfit">
            Join thousands of traders who chose knowledge over luck. Your first step starts today.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/login')} 
              size="lg" 
              className="w-full sm:w-auto bg-gradient-to-r from-[#00FF88] to-[#00C853] text-black font-bold hover:shadow-[0_0_25px_rgba(0,255,136,0.5)] border border-transparent rounded-sm px-8 text-base transition-all"
            >
              Enroll Now
            </Button>
            
            <a 
              href="/brochure.pdf" 
              download 
              className="w-full sm:w-auto"
            >
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full border-[#00FF88]/30 hover:border-[#00FF88] hover:text-[#00FF88] text-white hover:bg-[#00FF88]/5 rounded-sm px-8 text-base transition-all"
              >
                Download Brochure
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <Footer SC={SC} />

    </div>
  );
};

export default AboutPage;
